import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { getConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getConfluenceContentAri.ts';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { isViewModeSuggestionsEnabled } from './isViewModeSuggestionsEnabled.ts';
import {
	getCachedPublishedSuggestions,
	subscribeToPublishedSuggestions,
} from './published/publishedSuggestionsStore.ts';
import { useShowSuggestionsPanel } from './useShowSuggestionsPanel.ts';
import { useUnseenSuggestions } from './useUnseenSuggestions.ts';

type ReviewButtonMode = 'publishedViewMode' | 'liveDocViewMode' | 'editing' | 'unavailable';

type ReviewButtonActions = { onStartThinking: () => void };

type EditorReviewButtonActions = ReviewButtonActions & {
	/** The editor's own Review dot, set when it decides the dot itself. */
	showNotificationDot?: boolean;
	/** The dot inputs, set instead when the editor leaves the dot to this hook. */
	latestSuggestionCreatedAt?: string;
	isViewingSuggestions?: boolean;
};

const getNoSuggestions = (): undefined => undefined;

export type UseReviewButtonParams = {
	contentId: string;
	contentType: string | null | undefined;
	pageMode: PageMode | undefined;
	isLiveViewMode: boolean;
	isUnpublishedDraft: boolean;
	/** Eligibility from editor configuration, available before the plugin API initializes. */
	hasReviewButton: boolean;
	hasPublishedPageReviewButton: boolean;
	/** The editor's Review actions, set once its AI suggestions plugin is ready. */
	aiSuggestionsButtonActions: EditorReviewButtonActions | undefined;
};

const getReviewButtonMode = ({
	contentType,
	pageMode,
	isLiveViewMode,
	isUnpublishedDraft,
	hasPublishedPageReviewButton,
}: Pick<
	UseReviewButtonParams,
	| 'contentType'
	| 'pageMode'
	| 'isLiveViewMode'
	| 'isUnpublishedDraft'
	| 'hasPublishedPageReviewButton'
>): ReviewButtonMode => {
	const isPublishedPageOrBlogpost =
		(contentType === 'page' || contentType === 'blogpost') && !isUnpublishedDraft;

	if (pageMode === PageMode.VIEW) {
		return hasPublishedPageReviewButton && isPublishedPageOrBlogpost
			? 'publishedViewMode'
			: 'unavailable';
	}
	if (pageMode === PageMode.LIVE && isLiveViewMode) {
		return isPublishedPageOrBlogpost ? 'liveDocViewMode' : 'unavailable';
	}
	if (pageMode === PageMode.EDIT || pageMode === PageMode.LIVE) {
		return 'editing';
	}
	return 'unavailable';
};

const getHasReviewButtonForMode = ({
	mode,
	canReviewInViewMode,
	hasEditorReviewButton,
	isEligibleForSuggestedEdits,
}: {
	mode: ReviewButtonMode;
	canReviewInViewMode: boolean;
	hasEditorReviewButton: boolean;
	isEligibleForSuggestedEdits: boolean;
}): boolean => {
	switch (mode) {
		case 'publishedViewMode':
		case 'liveDocViewMode':
			return canReviewInViewMode;
		case 'editing':
			return (
				hasEditorReviewButton &&
				isEligibleForSuggestedEdits &&
				expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true)
			);
		case 'unavailable':
			return false;
	}
};

export type PublishedViewModeSuggestionsPrefetch = { contentId: string; contentAri: string };

/**
 * Whether the object sidebar shows Review, and the actions it runs. In published view mode Review
 * opens the Suggestions panel directly. Elsewhere it uses the editor's actions, including in live
 * doc view mode, where the editor stays mounted.
 *
 * Also returns whether Review shows the new suggestions dot. It is decided the same way in every
 * mode, from when the newest suggestion was created and whether the user is viewing suggestions.
 * The editor supplies these while it is mounted. In published view mode, where it isn't, they come
 * from the suggestions loaded by the prefetch, rendered from `publishedViewModeSuggestionsPrefetch`,
 * or by the panel.
 */
export const useReviewButton = ({
	contentId,
	contentType,
	pageMode,
	isLiveViewMode,
	isUnpublishedDraft,
	hasReviewButton,
	hasPublishedPageReviewButton,
	aiSuggestionsButtonActions,
}: UseReviewButtonParams): {
	hasReviewButtonForMode: boolean;
	reviewButtonActions: ReviewButtonActions | undefined;
	/** Whether Review shows the new suggestions dot, decided the same way in every mode. */
	showNotificationDot: boolean;
	publishedViewModeSuggestionsPrefetch: PublishedViewModeSuggestionsPrefetch | undefined;
} => {
	const { activationId, cloudId, isAdminHubAIEnabled, isAIEarlyAccessEnabled } = useSessionData();
	const isEligibleForSuggestedEdits = !!isAdminHubAIEnabled && !!isAIEarlyAccessEnabled;
	const hasEditorReviewButton = !!aiSuggestionsButtonActions || hasReviewButton;
	const mode = getReviewButtonMode({
		contentType,
		pageMode,
		isLiveViewMode,
		isUnpublishedDraft,
		hasPublishedPageReviewButton,
	});

	const isPublishedViewMode = mode === 'publishedViewMode';
	// Review in view mode needs edit permission, as suggestions are acted on in edit mode. A live
	// doc also needs the editor's Review button, as Review there uses the editor's actions.
	const isEligibleForReviewInViewMode =
		isEligibleForSuggestedEdits &&
		(isPublishedViewMode || (mode === 'liveDocViewMode' && hasEditorReviewButton));
	const canUpdateContent = useCanPerformContentOperation({
		// An empty id skips the permission query.
		contentId: isEligibleForReviewInViewMode ? contentId : '',
		contentType: contentType === 'blogpost' ? 'blogpost' : 'page',
		operation: OperationTypes.UPDATE,
	});
	const canReviewInViewMode =
		isEligibleForReviewInViewMode && !!canUpdateContent && isViewModeSuggestionsEnabled();
	const canReviewPublishedViewMode = isPublishedViewMode && canReviewInViewMode;

	const { showSuggestionsPanel, isSuggestionsPanelShown } = useShowSuggestionsPanel();
	const showPublishedSuggestionsPanel = useCallback(
		() => showSuggestionsPanel({ publishedContentId: contentId }),
		[contentId, showSuggestionsPanel],
	);
	const publishedContentAri = getConfluenceContentAri({
		siteId: cloudId,
		resourceType: contentType === 'blogpost' ? 'blogpost' : 'page',
		activationId,
		resourceId: contentId,
	});
	// The dot is decided as in edit mode, from the suggestions the prefetch or panel loaded.
	const publishedSuggestions = useSyncExternalStore(
		subscribeToPublishedSuggestions,
		() =>
			canReviewPublishedViewMode ? getCachedPublishedSuggestions(publishedContentAri) : undefined,
		getNoSuggestions,
	);
	const latestPublishedSuggestionCreatedAt = useMemo(() => {
		let latest: string | undefined;
		publishedSuggestions?.forEach(({ createdAt }) => {
			if (createdAt && (!latest || createdAt > latest)) {
				latest = createdAt;
			}
		});
		return latest;
	}, [publishedSuggestions]);
	const { hasUnseenSuggestions, markSuggestionsSeen } = useUnseenSuggestions(
		contentId,
		isPublishedViewMode
			? latestPublishedSuggestionCreatedAt
			: aiSuggestionsButtonActions?.latestSuggestionCreatedAt,
	);
	// Opening the panel, or selecting a suggestion while it's closed, counts as viewing them.
	const isViewingSuggestions = isPublishedViewMode
		? isSuggestionsPanelShown
		: !!aiSuggestionsButtonActions?.isViewingSuggestions;

	// Viewing the suggestions marks them seen and clears the dot. Only written while there is
	// something unseen, so it does not re-fire. There is only something unseen once Review in view
	// mode is enabled, as the inputs are only set then, so this is not an ungated side effect.
	useEffect(() => {
		if (hasUnseenSuggestions && isViewingSuggestions) {
			markSuggestionsSeen();
		}
	}, [hasUnseenSuggestions, isViewingSuggestions, markSuggestionsSeen]);

	return {
		showNotificationDot: isPublishedViewMode
			? hasUnseenSuggestions
			: (aiSuggestionsButtonActions?.showNotificationDot ?? hasUnseenSuggestions),
		publishedViewModeSuggestionsPrefetch: canReviewPublishedViewMode
			? { contentId, contentAri: publishedContentAri }
			: undefined,
		hasReviewButtonForMode: getHasReviewButtonForMode({
			mode,
			canReviewInViewMode,
			hasEditorReviewButton,
			isEligibleForSuggestedEdits,
		}),
		reviewButtonActions: isPublishedViewMode
			? canReviewPublishedViewMode
				? { onStartThinking: showPublishedSuggestionsPanel }
				: undefined
			: aiSuggestionsButtonActions,
	};
};
